/**
 * LoginPageLaunch — Login del SSO Launcher
 *
 * Ruta: /launch-login
 *
 * Espeja el flujo de ButtonGoogle:
 *   Google OAuth → Google userinfo → Cognito roles → Redux → navigate(/launch)
 *
 * Cuando el usuario llega a /launch, Redux ya tiene perfil + roles + countriesOptions.
 */

// libraries
import { useEffect, useState } from 'react';
import { useGoogleLogin, TokenResponse, GoogleOAuthProvider } from '@react-oauth/google';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { monserratSemiBold, robotoRegular } from '@habitech/shared';

// domain
import {
  setUser,
  setCountry,
  setRolesFromCognito,
  setCountriesOptions,
  useLazyGetUserRoleQuery,
} from '@domains/user';
import { getCountryCodeByEmail } from '@domains/country';

// assets
import { Logo } from '@habitech/keychain-atoms-display';

// components
import { AutoLoginLoader } from '../LoginPage/components';

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
const PageBg = styled.div`
  min-height: 100vh;
  background: #f4f5f7;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 2.5rem 2.25rem;
  width: 100%;
  max-width: 380px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.75rem;
`;

const AppTitle = styled.h1`
  margin: 0;
  margin-top: 8px;
  font-size: 28px;
  font-weight: 700;
  font-family: ${monserratSemiBold};
  color: #111827;
  letter-spacing: -0.3px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 0 0 1.5rem 0;
`;

const Description = styled.p`
  margin: 0 0 1.5rem 0;
  font-size: 13px;
  font-family: ${robotoRegular};
  color: #6b7280;
  text-align: center;
  line-height: 1.5;
`;

const GoogleBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, #7c3aed, #5b21b6);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 11px 20px;
  font-size: 14px;
  font-family: ${robotoRegular};
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover { opacity: 0.88; }
`;

const ErrorBox = styled.div`
  margin-top: 1rem;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fca5a5;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-family: ${robotoRegular};
  text-align: center;
`;

const Footer = styled.p`
  margin-top: 1.5rem;
  font-size: 12px;
  font-family: ${robotoRegular};
  color: #9ca3af;
  text-align: center;
`;

// ─────────────────────────────────────────────────────────────────────────────
// localStorage — claves exclusivas del launcher
// ─────────────────────────────────────────────────────────────────────────────
import {
  LAUNCH_TOKEN_KEY,
  LAUNCH_GOOGLE_ID_KEY,
  LAUNCH_PROFILE_KEY,
  LAUNCH_EXPIRY_KEY,
  LAUNCH_SESSION_TTL_MS,
} from './launch.constants';

const saveLaunchStorage = (
  token: string,
  googleId: string,
  email: string,
  name: string,
  lastName: string,
  imageProfile: string,
): void => {
  localStorage.setItem(LAUNCH_TOKEN_KEY, token);
  localStorage.setItem(LAUNCH_GOOGLE_ID_KEY, googleId);
  localStorage.setItem(LAUNCH_PROFILE_KEY, JSON.stringify({ email, name, lastName, imageProfile }));
  localStorage.setItem(LAUNCH_EXPIRY_KEY, String(Date.now() + LAUNCH_SESSION_TTL_MS));
};

// ─────────────────────────────────────────────────────────────────────────────
// Botón Google — solo dispara OAuth y devuelve el perfil
// ─────────────────────────────────────────────────────────────────────────────
interface GoogleProfile {
  sub: string;
  email: string;
  given_name: string;
  family_name: string;
  picture: string;
  name: string;
}

interface InternalGoogleButtonProps {
  onSuccess: (token: string, profile: GoogleProfile) => void;
  onError: () => void;
  loading: boolean;
}

const InternalGoogleButton = ({ onSuccess, onError, loading }: InternalGoogleButtonProps) => {
  const loginGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse: TokenResponse) => {
      try {
        const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        const profile: GoogleProfile = await res.json();
        onSuccess(tokenResponse.access_token, profile);
      } catch {
        onError();
      }
    },
    onError,
  });

  if (loading) {
    return (
      <GoogleBtn disabled style={{ opacity: 0.7, cursor: 'default' }}>
        <Spinner animation="border" size="sm" />
        Autenticando...
      </GoogleBtn>
    );
  }

  return (
    <GoogleBtn onClick={() => loginGoogle()}>
      <svg width="18" height="18" viewBox="0 0 24 24">
        <path fill="white" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="white" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="white" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="white" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
      Continuar con Google
    </GoogleBtn>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
const LoginPageLaunchInner = () => {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const [error, setError]     = useState(false);
  const [logging, setLogging] = useState(false);

  const [triggerCognito, {
    data: cognitoData,
    isSuccess: cognitoSuccess,
    isError: cognitoError,
  }] = useLazyGetUserRoleQuery();

  // Auto-login: si hay sesión válida en localStorage, re-hidrata Redux y redirige
  useEffect(() => {
    const token     = localStorage.getItem(LAUNCH_TOKEN_KEY);
    const googleId  = localStorage.getItem(LAUNCH_GOOGLE_ID_KEY);
    const profileRaw = localStorage.getItem(LAUNCH_PROFILE_KEY);
    const expiry    = localStorage.getItem(LAUNCH_EXPIRY_KEY);

    if (!token || !googleId || !profileRaw || !expiry) return;
    if (Date.now() >= Number(expiry)) return;

    try {
      const profile = JSON.parse(profileRaw);
      setLogging(true);
      dispatch(setUser({
        email:      profile.email,
        givenName:  profile.name,
        familyName: profile.lastName,
        googleId,
        imageUrl:   profile.imageProfile,
        name:       `${profile.name} ${profile.lastName}`,
      }));
      const country = getCountryCodeByEmail(profile.email);
      if (country) {
        dispatch(setCountry(country));
        triggerCognito({ country, userId: googleId, external: false });
      } else {
        navigate('/launch', { replace: true });
      }
    } catch {
      // Si el perfil guardado está corrupto, mostramos el login normal
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando Cognito responde → despachar roles + países → navegar
  useEffect(() => {
    if (cognitoSuccess && cognitoData) {
      dispatch(setRolesFromCognito(cognitoData.roles));
      dispatch(setCountriesOptions(cognitoData.countriesSelect));
      if (cognitoData.countriesByUser[0]) {
        dispatch(setCountry(cognitoData.countriesByUser[0]));
      }
      navigate('/launch');
    }
  }, [cognitoSuccess, cognitoData, dispatch, navigate]);

  useEffect(() => {
    if (cognitoError) {
      setLogging(false);
      setError(true);
    }
  }, [cognitoError]);

  const handleGoogleSuccess = (token: string, profile: GoogleProfile) => {
    setLogging(true);

    // 1. Despachar perfil a Redux
    dispatch(
      setUser({
        email:      profile.email,
        givenName:  profile.given_name,
        familyName: profile.family_name,
        googleId:   profile.sub,
        imageUrl:   profile.picture,
        name:       profile.name,
      }),
    );

    // 2. Guardar en localStorage para SSO hacia herramientas externas
    saveLaunchStorage(
      token,
      profile.sub,
      profile.email,
      profile.given_name,
      profile.family_name,
      profile.picture,
    );

    // 3. Derivar país del email y llamar a Cognito
    try {
      const country = getCountryCodeByEmail(profile.email);
      if (country) {
        dispatch(setCountry(country));
        triggerCognito({ country, userId: profile.sub, external: false });
      } else {
        navigate('/launch');
      }
    } catch {
      navigate('/launch');
    }
  };

  const handleError = () => {
    setLogging(false);
    setError(true);
  };

  if (logging) {
    return (
      <PageBg>
        <AutoLoginLoader
          title="Preparando Portal IdM..."
          subtitle="Validando roles y permisos"
        />
      </PageBg>
    );
  }

  return (
    <PageBg>
      <Card>
        <CardHeader>
          <div style={{ zoom: 1.2, lineHeight: 0 }}>
            <Logo size="small" type="habi" variant="color" />
          </div>
          <AppTitle>Portal IdM</AppTitle>
        </CardHeader>

        <Divider />

        <Description>
          Inicia sesión para acceder a los módulos<br />y herramientas de Inteligencia de Mercados
        </Description>

        <InternalGoogleButton
          onSuccess={handleGoogleSuccess}
          onError={handleError}
          loading={logging}
        />

        {error && (
          <ErrorBox>Error al iniciar sesión. Intenta de nuevo.</ErrorBox>
        )}
      </Card>

      <Footer>© {new Date().getFullYear()} Habi · Portal IdM</Footer>
    </PageBg>
  );
};

export const LoginPageLaunch = () => {
  const authKey = process.env.ENV_GOOGLE_AUTH;

  if (!authKey) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: '100vh' }}
      >
        <div className="text-danger text-center">
          <strong>Error:</strong> No se encontró ENV_GOOGLE_AUTH en las variables de entorno.
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={authKey}>
      <LoginPageLaunchInner />
    </GoogleOAuthProvider>
  );
};
